import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useData } from '../../data/DataProvider';
import { date, money, invoiceTotal } from '../../shared/formatters';
import SearchInput from '../../shared/components/SearchInput';
import EmptyState from '../../shared/components/EmptyState';
import Skeleton from '../../shared/components/Skeleton';

const STATUS_OPTIONS = [
  ['all', 'All statuses'],
  ['draft', 'Draft'],
  ['sent', 'Awaiting approval'],
  ['approved', 'Approved'],
  ['declined', 'Declined'],
  ['expired', 'Expired'],
];

// Quotes list (pass 5 §5E) — search + status filter over the quotes
// workflow collection. Quote statuses map onto the shared status-pill
// palette (approved → paid-green, declined → overdue-red, expired →
// cancelled-grey).
function QuoteStatusPill({ status }) {
  const palette = { draft: 'draft', sent: 'sent', approved: 'paid', declined: 'overdue', expired: 'cancelled' };
  const display = status === 'sent' ? 'awaiting approval' : status;
  return <span className={`status-pill status-${palette[status] || 'draft'}`}><span className="status-dot" />{display}</span>;
}

export { QuoteStatusPill };

export default function QuotesList() {
  const { data } = useData();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 200);
    return () => clearTimeout(timer);
  }, []);

  const filtered = useMemo(() => data.quotes.filter((quote) => {
    const client = data.clients.find((item) => item.id === quote.client_id);
    const haystack = `${quote.number} ${quote.title} ${client?.name || ''}`.toLowerCase();
    return (!query || haystack.includes(query.toLowerCase()))
      && (status === 'all' || quote.status === status);
  }), [data.quotes, data.clients, query, status]);

  const awaiting = data.quotes.filter((quote) => quote.status === 'sent').length;

  if (loading) return <div className="standalone-page"><Skeleton rows={6} /></div>;

  return (
    <div className="standalone-page">
      <header className="simple-header">
        <div>
          <span className="eyebrow">Estimates &amp; approvals</span>
          <h1>Quotes</h1>
          <p className="field-hint">{awaiting} awaiting client approval · approved quotes convert to a job or invoice in one click.</p>
        </div>
        <div className="header-actions">
          <button className="primary-button" onClick={() => navigate('/admin/quotes/new')}>+ New quote</button>
        </div>
      </header>

      <div className="toolbar">
        <SearchInput value={query} onChange={setQuery} placeholder="Search quote number, client or description…" />
        <select value={status} onChange={(event) => setStatus(event.target.value)} className="engineer-filter" aria-label="Filter by status">
          {STATUS_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
      </div>

      {!data.quotes.length ? (
        <EmptyState title="No quotes yet" message="Create a quote from a client record or the button above." action="New quote" onAction={() => navigate('/admin/quotes/new')} />
      ) : !filtered.length ? (
        <EmptyState title="No matching quotes" message="Try changing your search or filters." />
      ) : (
        <section className="panel no-padding invoice-list">
          {filtered.map((quote) => (
            <button type="button" className="invoice-row" style={{ width: '100%', textAlign: 'left', cursor: 'pointer' }} key={quote.id} onClick={() => navigate(`/admin/quotes/${quote.id}`)}>
              <div>
                <strong>{quote.number}</strong>
                <span>{data.clients.find((item) => item.id === quote.client_id)?.name || 'Unknown client'}</span>
              </div>
              <span>{quote.title}</span>
              <span>{quote.valid_until ? `Valid until ${date(quote.valid_until)}` : '—'}</span>
              <strong>{money(invoiceTotal(quote))}</strong>
              <QuoteStatusPill status={quote.status} />
            </button>
          ))}
        </section>
      )}
    </div>
  );
}
